import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

// Paid-tier ordering — mirrors the backend's paidTierRank. A move to a lower
// rank is a downgrade: scheduled for the end of the paid period, never
// charged today.
const PLAN_RANK = { SOLO: 1, HIGH_STREET: 2, CHAIN: 3 };

const PLAN_TIERS = [
  {
    id: 'SOLO',
    priceLabel: '€19',
    cadence: 'per month',
    features: ['1 office', 'Up to 2 staff', '7 days of analytics'],
  },
  {
    id: 'HIGH_STREET',
    priceLabel: '€39',
    cadence: 'per month',
    highlight: true,
    features: [
      'Up to 3 offices',
      'Up to 10 staff',
      '30 days of analytics',
      'Heatmap + staff performance',
      'Dynamic QR codes',
    ],
  },
  {
    id: 'CHAIN',
    priceLabel: '€69',
    cadence: 'per month',
    features: [
      'Up to 10 offices',
      'Unlimited staff',
      '90 days of analytics',
      'CSV export',
      'All High Street features',
    ],
  },
];

function StatusBadge({ status }) {
  if (!status) return null;
  const palette = status === 'active' || status === 'trialing'
    ? { bg: '#D1FAE5', fg: '#047857' }
    : status === 'past_due' || status === 'unpaid'
      ? { bg: '#FEE2E2', fg: '#B91C1C' }
      : { bg: '#E2E8F0', fg: '#475569' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, background: palette.bg, color: palette.fg, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export default function BillingTab({ token, isOwner }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [error, setError] = useState('');
  const [verifyModal, setVerifyModal] = useState(false);
  const [resend, setResend] = useState('idle');
  const [confirmPlan, setConfirmPlan] = useState(null);
  // Success dialog after a downgrade was scheduled — the change is invisible
  // (plan stays the same until period end), so a banner alone reads as
  // "nothing happened".
  const [scheduledModal, setScheduledModal] = useState(null);

  const loadStatus = useCallback(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_BASE}/api/v1/billing/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setStatus(data); })
      .catch(() => setError(t('billing.loadError')))
      .finally(() => setLoading(false));
  }, [token, t]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Surface checkout redirect result from the URL query.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('checkout');
    if (result === 'success') setBanner({ kind: 'ok', msg: t('billing.successBanner') });
    if (result === 'cancel') setBanner({ kind: 'info', msg: t('billing.cancelBanner') });
  }, [location.search, t]);

  const currentPlanForRank = status?.plan || 'SOLO';
  const startCheckout = useCallback((plan) => {
    setError('');
    setConfirmPlan(null);
    fetch(`${API_BASE}/api/v1/billing/checkout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, locale: i18n.language }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          if (body.error === 'EMAIL_NOT_VERIFIED') { setVerifyModal(true); return null; }
          throw new Error(body.message || t('billing.checkoutError'));
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        // In-place plan change: the subscription was already updated on the
        // server (no Stripe redirect; upgrades charge the prorated difference
        // to the card on file, downgrades are scheduled for period end).
        // Refresh billing state and confirm — do NOT navigate, which would
        // reload the page and re-surface the flow.
        if (data.applied) {
          const targetRank = PLAN_RANK[plan] || 0;
          const currentRank = PLAN_RANK[currentPlanForRank] || 0;
          const isScheduledDowngrade = plan !== currentPlanForRank && targetRank < currentRank;
          const msg = plan === currentPlanForRank ? t('billing.scheduleCanceledBanner')
            : isScheduledDowngrade ? t('billing.changeScheduledBanner')
              : t('billing.successBanner');
          setBanner({ kind: 'ok', msg });
          if (isScheduledDowngrade) {
            setScheduledModal({
              fromPlan: currentPlanForRank,
              toPlan: plan,
              date: status?.currentPeriodEnd || null,
            });
          }
          loadStatus();
          return;
        }
        if (data.url) window.location.assign(data.url);
      })
      .catch((err) => setError(err.message));
  }, [token, t, i18n.language, loadStatus, currentPlanForRank, status?.currentPeriodEnd]);

  const openPortal = useCallback(() => {
    setError('');
    fetch(`${API_BASE}/api/v1/billing/portal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          if (body.error === 'EMAIL_NOT_VERIFIED') { setVerifyModal(true); return null; }
          throw new Error(body.message || t('billing.portalError'));
        }
        return r.json();
      })
      .then((data) => {
        if (data && data.url) window.location.assign(data.url);
      })
      .catch((err) => setError(err.message));
  }, [token, t]);

  const handleResend = useCallback(() => {
    setResend('sending');
    fetch(`${API_BASE}/api/v1/auth/resend-verification`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setResend(r.ok ? 'sent' : 'error'))
      .catch(() => setResend('error'));
  }, [token]);

  const currentPlan = status?.plan || 'SOLO';
  const hasCustomer = status?.hasStripeCustomer;
  const stripeConfigured = status?.stripeConfigured;
  // A tenant can be "on SOLO" by default (no subscription) or by having
  // an active SOLO subscription. The latter shouldn't see an upgrade CTA
  // for the same tier; the former should.
  const hasActiveSubscription = status?.subscriptionStatus === 'active'
    || status?.subscriptionStatus === 'trialing';
  // Mirrors the backend's LIVE_SUBSCRIPTION_STATUSES: a plan change for these
  // is applied in place (prorated, card on file) — no Stripe Checkout redirect.
  const hasLiveSubscription = ['active', 'trialing', 'past_due'].includes(status?.subscriptionStatus);

  // Exact prorated charge for the pending confirm dialog, fetched from the
  // Stripe invoice preview so the dialog can say "charged €X now" instead of
  // the vaguer "we'll charge the difference".
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    setPreview(null);
    if (!confirmPlan || !hasLiveSubscription) return undefined;
    const isDown = (PLAN_RANK[confirmPlan.id] || 0) < (PLAN_RANK[currentPlan] || 0);
    if (isDown) return undefined; // downgrade copy already states €0 today + date
    let cancelled = false;
    fetch(`${API_BASE}/api/v1/billing/change-preview?plan=${confirmPlan.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data && data.available) setPreview(data);
      })
      .catch(() => {}); // dialog falls back to the generic prorated copy
    return () => { cancelled = true; };
  }, [confirmPlan, hasLiveSubscription, currentPlan, token]);
  const trialDaysRemaining = status?.trialDaysRemaining ?? 0;
  const trialActive = !hasActiveSubscription && trialDaysRemaining > 0;
  const trialExpired = !hasActiveSubscription && !trialActive
    && status?.trialEndsAt && new Date(status.trialEndsAt) < new Date();

  return (
    <div>
      {banner && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem',
          background: banner.kind === 'ok' ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)',
          color: banner.kind === 'ok' ? '#10B981' : '#A5B4FC',
          border: '1px solid rgba(148,163,184,0.2)',
          fontSize: '0.88rem',
        }}>{banner.msg}</div>
      )}

      {trialActive && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem',
          background: 'rgba(59,130,246,0.12)', color: '#93C5FD',
          border: '1px solid rgba(59,130,246,0.3)', fontSize: '0.88rem',
        }}>
          {t('billing.trialRemaining', { count: trialDaysRemaining })}
        </div>
      )}
      {trialExpired && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem',
          background: 'rgba(251,191,36,0.12)', color: '#FCD34D',
          border: '1px solid rgba(251,191,36,0.3)', fontSize: '0.88rem',
        }}>
          {t('billing.trialExpired')}
        </div>
      )}

      <section style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: '#F8FAFC' }}>{t('billing.currentPlan')}</h3>
            <div style={{ marginTop: 4, color: '#94A3B8', fontSize: '0.85rem' }}>{t(`billing.plans.${currentPlan}.name`)}</div>
          </div>
          <StatusBadge status={status?.subscriptionStatus} />
        </div>
        {status?.currentPeriodEnd && (
          <p style={styles.meta}>
            {t(
              status?.cancelAtPeriodEnd ? 'billing.accessUntil'
                : status?.subscriptionStatus === 'active' ? 'billing.paidUntil'
                  : 'billing.renewsOn',
              { date: new Date(status.currentPeriodEnd).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) },
            )}
          </p>
        )}
        {status?.pendingPlan && (
          <div style={{
            padding: '0.6rem 0.9rem', borderRadius: 8, margin: '0 0 0.75rem',
            background: 'rgba(251,191,36,0.12)', color: '#FCD34D',
            border: '1px solid rgba(251,191,36,0.3)', fontSize: '0.85rem',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <span>
              {t('billing.pendingDowngrade', {
                plan: t(`billing.plans.${status.pendingPlan}.name`),
                date: status.pendingPlanEffectiveAt
                  ? new Date(status.pendingPlanEffectiveAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
                  : '',
              })}
            </span>
            {isOwner && (
              <button
                type="button"
                onClick={() => startCheckout(currentPlan)}
                style={{ ...styles.btnOutline, width: 'auto', padding: '0.35rem 0.75rem', fontSize: '0.8rem', borderColor: 'rgba(251,191,36,0.5)' }}
              >
                {t('billing.keepCurrentPlan')}
              </button>
            )}
          </div>
        )}
        {!stripeConfigured && (
          <p style={{ ...styles.meta, color: '#FBBF24' }}>{t('billing.notConfigured')}</p>
        )}
        {hasCustomer && (
          <button style={styles.btnOutline} onClick={openPortal} disabled={!isOwner}>
            {t('billing.managePortal')}
          </button>
        )}
      </section>

      {error && (
        <div style={{ color: '#FCA5A5', fontSize: '0.88rem', marginBottom: '0.75rem' }}>{error}</div>
      )}
      {loading && <p style={styles.meta}>{t('common.loading')}</p>}

      <div style={styles.grid}>
        {PLAN_TIERS.map((tier) => {
          // "Current" means the tenant both holds this plan AND is paying for
          // it — a free-Starter tenant is not a paid Starter customer and
          // should still see a purchase CTA for the Starter tier.
          const isCurrent = tier.id === currentPlan
            && (tier.id !== 'SOLO' || hasActiveSubscription);
          // A pending end-of-period downgrade targets this tier.
          const isScheduled = tier.id === status?.pendingPlan;
          const scheduledDateFmt = isScheduled && status?.pendingPlanEffectiveAt
            ? new Date(status.pendingPlanEffectiveAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
            : '';
          const canUpgrade = isOwner && !isCurrent;
          // The current plan card stands out (emerald border + tint); when a
          // downgrade is pending it also announces how long it stays valid.
          const currentUntilFmt = isCurrent && status?.pendingPlan && status?.pendingPlanEffectiveAt
            ? new Date(status.pendingPlanEffectiveAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
            : '';
          return (
            <section
              key={tier.id}
              style={{
                ...styles.card,
                borderColor: isCurrent ? '#10B981' : isScheduled ? 'rgba(251,191,36,0.6)' : tier.highlight ? '#7C3AED' : '#334155',
                ...(isCurrent ? {
                  borderWidth: 2,
                  background: 'linear-gradient(180deg, rgba(16,185,129,0.10), rgba(16,185,129,0.02)), #1E293B',
                  boxShadow: '0 4px 24px rgba(16,185,129,0.15)',
                } : {}),
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ margin: 0, color: '#F8FAFC' }}>{t(`billing.plans.${tier.id}.name`)}</h3>
                {isCurrent ? (
                  <span style={styles.currentBadge}>{t('billing.yourPlan')}</span>
                ) : tier.highlight ? (
                  <span style={styles.recommendBadge}>{t('billing.recommended')}</span>
                ) : null}
              </div>
              <div style={{ margin: '0.5rem 0 1rem', color: '#F8FAFC' }}>
                <span style={{ fontSize: '1.6rem', fontWeight: 800 }}>{tier.priceLabel}</span>
                <span style={{ color: '#94A3B8', fontSize: '0.85rem', marginLeft: 4 }}>{t(`billing.cadences.${tier.cadence.replace(/ /g, '_')}`)}</span>
              </div>
              {currentUntilFmt && (
                <div style={styles.validUntilChip}>
                  {t('billing.validUntil', { date: currentUntilFmt })}
                </div>
              )}
              <ul style={styles.featureList}>
                {tier.features.map((f) => (
                  <li key={f} style={styles.featureItem}>{f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled style={{ ...styles.btnOutline, opacity: 0.5, cursor: 'default' }}>
                  {t('billing.yourPlan')}
                </button>
              ) : isScheduled ? (
                <button disabled style={{ ...styles.btnOutline, cursor: 'default', borderColor: 'rgba(251,191,36,0.5)', color: '#FCD34D' }}>
                  {t('billing.scheduledFrom', { date: scheduledDateFmt })}
                </button>
              ) : canUpgrade ? (
                <button
                  style={tier.highlight ? styles.btnPrimary : styles.btnOutline}
                  onClick={() => setConfirmPlan(tier)}
                >
                  {tier.id === currentPlan
                    ? t('billing.subscribeTo', { plan: t(`billing.plans.${tier.id}.name`) })
                    : t('billing.upgradeTo', { plan: t(`billing.plans.${tier.id}.name`) })}
                </button>
              ) : (
                <button disabled style={{ ...styles.btnOutline, opacity: 0.5, cursor: 'default' }}>
                  {!isOwner ? t('billing.ownerOnly') : t('billing.notAvailable')}
                </button>
              )}
            </section>
          );
        })}
      </div>

      {confirmPlan && (() => {
        const upgradeIsSolo = confirmPlan.id === 'SOLO';
        const dateFmt = status?.trialEndsAt
          ? new Date(status.trialEndsAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
          : '';
        const isDowngrade = hasLiveSubscription
          && (PLAN_RANK[confirmPlan.id] || 0) < (PLAN_RANK[currentPlan] || 0);
        const periodEndFmt = status?.currentPeriodEnd
          ? new Date(status.currentPeriodEnd).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
          : '';
        let body;
        if (isDowngrade) {
          body = t('billing.confirmDowngrade', {
            currentPlan: t(`billing.plans.${currentPlan}.name`),
            plan: t(`billing.plans.${confirmPlan.id}.name`),
            price: confirmPlan.priceLabel,
            date: periodEndFmt,
          });
        } else if (hasLiveSubscription && preview && preview.immediate && preview.amountDueCents > 0) {
          const amount = new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: (preview.currency || 'eur').toUpperCase(),
          }).format(preview.amountDueCents / 100);
          body = t('billing.confirmProratedExact', {
            plan: t(`billing.plans.${confirmPlan.id}.name`),
            amount,
            price: confirmPlan.priceLabel,
          });
        } else if (hasLiveSubscription) {
          body = t('billing.confirmProratedChange', { plan: t(`billing.plans.${confirmPlan.id}.name`), price: confirmPlan.priceLabel });
        } else if (upgradeIsSolo && trialActive) {
          body = t('billing.confirmSoloWithTrial', { count: trialDaysRemaining, price: confirmPlan.priceLabel, date: dateFmt });
        } else if (trialActive) {
          body = t('billing.confirmCrossGradeFromTrial', { plan: t(`billing.plans.${confirmPlan.id}.name`), price: confirmPlan.priceLabel });
        } else {
          body = t('billing.confirmChargeToday', { price: confirmPlan.priceLabel });
        }
        return (
          <div style={styles.modalOverlay} onClick={() => setConfirmPlan(null)}>
            <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 0.5rem', color: '#F8FAFC' }}>{t('billing.confirmTitle')}</h3>
              <p style={{ margin: '0 0 1rem', color: '#CBD5E1', fontSize: '0.9rem', lineHeight: 1.5 }}>{body}</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setConfirmPlan(null)} style={styles.btnOutline}>
                  {t('billing.confirmCancel')}
                </button>
                <button type="button" onClick={() => startCheckout(confirmPlan.id)} style={styles.btnPrimary}>
                  {hasLiveSubscription ? t('billing.confirmApplyChange') : t('billing.confirmContinue')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {scheduledModal && (() => {
        const dateFmt = scheduledModal.date
          ? new Date(scheduledModal.date).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
          : '';
        return (
          <div style={styles.modalOverlay} onClick={() => setScheduledModal(null)}>
            <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.5rem' }}>
                <span aria-hidden="true" style={{
                  width: 28, height: 28, borderRadius: '50%', background: 'rgba(16,185,129,0.18)',
                  color: '#10B981', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1rem', fontWeight: 800, flexShrink: 0,
                }}>✓</span>
                <h3 style={{ margin: 0, color: '#F8FAFC' }}>{t('billing.scheduledModalTitle')}</h3>
              </div>
              <p style={{ margin: '0 0 1rem', color: '#CBD5E1', fontSize: '0.9rem', lineHeight: 1.5 }}>
                {t('billing.scheduledModalBody', {
                  currentPlan: t(`billing.plans.${scheduledModal.fromPlan}.name`),
                  plan: t(`billing.plans.${scheduledModal.toPlan}.name`),
                  date: dateFmt,
                })}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setScheduledModal(null)} style={styles.btnPrimary}>
                  {t('billing.scheduledModalClose')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {verifyModal && (
        <div style={styles.modalOverlay} onClick={() => setVerifyModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem', color: '#F8FAFC' }}>{t('billing.verifyModalTitle')}</h3>
            <p style={{ margin: '0 0 1rem', color: '#CBD5E1', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {t('billing.verifyModalBody')}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setVerifyModal(false)} style={styles.btnOutline}>
                {t('billing.verifyModalClose')}
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resend === 'sending' || resend === 'sent'}
                style={styles.btnPrimary}
              >
                {resend === 'sent' ? t('billing.verifyResent')
                  : resend === 'sending' ? t('billing.verifyResending')
                  : t('billing.verifyResend')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    background: '#1E293B', borderRadius: 12, padding: '1.5rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', marginBottom: '1rem',
    border: '1px solid #334155',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  meta: { margin: '0 0 0.75rem', color: '#94A3B8', fontSize: '0.82rem' },
  featureList: { listStyle: 'none', margin: '0 0 1rem', padding: 0 },
  featureItem: { color: '#CBD5E1', fontSize: '0.88rem', padding: '0.25rem 0', display: 'flex', gap: 8 },
  btnPrimary: {
    padding: '0.65rem 1.25rem', borderRadius: 8, border: 'none',
    background: '#7C3AED', color: '#fff', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', width: '100%',
  },
  btnOutline: {
    padding: '0.65rem 1.25rem', borderRadius: 8, border: '1.5px solid #334155',
    background: 'transparent', color: '#F8FAFC', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', width: '100%',
  },
  recommendBadge: {
    background: 'rgba(124,58,237,0.2)', color: '#C4B5FD',
    padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem',
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
  },
  currentBadge: {
    background: 'rgba(16,185,129,0.22)', color: '#6EE7B7',
    padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem',
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
  },
  validUntilChip: {
    display: 'inline-block', background: 'rgba(251,191,36,0.15)', color: '#FCD34D',
    border: '1px solid rgba(251,191,36,0.35)', padding: '3px 10px', borderRadius: 999,
    fontSize: '0.76rem', fontWeight: 600, marginBottom: '0.9rem',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 500, padding: '1rem',
  },
  modalCard: {
    background: '#1E293B', border: '1px solid #334155', borderRadius: 14,
    padding: '1.5rem', maxWidth: 420, width: '100%',
    boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
  },
};
